import React from "react";
import { createRoot } from "react-dom/client";
import Widget from "./Widget.jsx";
import { logger } from "../shared/logger";

/* --- Preflight: purge any Google Maps scripts without a key, and keep purging if injected later --- */
(function purgeBadGoogleMapScripts() {
  const kill = (s) => { try { s.parentNode?.removeChild(s); } catch {} };

  const nuke = () => {
    const list = [...document.querySelectorAll('script[src*="maps.googleapis.com/maps/api/js"]')];
    for (const s of list) {
      try {
        const u = new URL(s.src, window.location.href);
        const k = (u.searchParams.get("key") || "").trim();
        if (!k) kill(s); // remove missing OR empty key
      } catch { kill(s); } // if URL parsing fails, remove to be safe
    }
  };

  // run immediately
  nuke();

  // keep watching for any future injections of a bad script (with timeout to prevent memory leak)
  const mo = new MutationObserver((muts) => {
    for (const m of muts) {
      for (const n of m.addedNodes) {
        if (n && n.tagName === "SCRIPT") {
          const src = n.getAttribute("src") || "";
          if (src.includes("maps.googleapis.com/maps/api/js")) {
            try {
              const u = new URL(src, window.location.href);
              const k = (u.searchParams.get("key") || "").trim();
              if (!k) kill(n);
            } catch { kill(n); }
          }
        }
      }
    }
  });
  mo.observe(document.documentElement || document, { childList: true, subtree: true });

  // Disconnect observer after 5 seconds to prevent memory leak
  // (bad scripts are typically injected during initial page load)
  setTimeout(() => {
    mo.disconnect();
  }, 5000);
})();

(function () {
  const truthy = (v, dflt = false) =>
    v == null ? dflt : /^(1|true|yes|on)$/i.test(String(v));

  function ready(fn) {
    if (document.readyState === "loading")
      document.addEventListener("DOMContentLoaded", fn);
    else fn();
  }

  function renderWidget(el) {
      /* 🔹 READ all config from data-attributes */
      const theme = (el.getAttribute("data-theme") || "light").toLowerCase();
      const columnsAttr = el.getAttribute("data-columns");
      const gap = parseInt(el.getAttribute("data-gap") || "12", 10);
      const radius = parseInt(el.getAttribute("data-radius") || "12", 10);
      const cardAspect = parseFloat(el.getAttribute("data-card-aspect") || "0.75");
      const shadow = truthy(el.getAttribute("data-shadow") || "true", true);

      const map = truthy(el.getAttribute("data-map") || "true", true);
      const mapHeightUnit = el.getAttribute("data-map-height-unit") || "px";
      const mapHeightRaw = el.getAttribute("data-map-height") || "520";
      const mapHeight = mapHeightUnit === "calc" ? mapHeightRaw : parseInt(mapHeightRaw, 10);

      /* 👇 NEW: choose Google vs Leaflet */
      const isGoogle = truthy(el.getAttribute("data-is-google") || "false", false);
      const googleApiKey = (el.getAttribute("data-google-api-key") || "").trim();

      /* 👇 NEW: read is-price-pins toggle */
      const isPricePins = truthy(el.getAttribute("data-is-price-pins") || "false", false);

      /* 👇 NEW: read clustering toggle */
      const enableClustering = truthy(el.getAttribute("data-enable-clustering") || "false", false);

      /* 👇 NEW: read cluster marker styles */
      const clusterStyle = {
        bgColor: el.getAttribute("data-cluster-bg-color") || "#fe5922",
        textColor: el.getAttribute("data-cluster-text-color") || "#ffffff",
        fontSize: parseInt(el.getAttribute("data-cluster-font-size") || "14", 10),
        fontWeight: parseInt(el.getAttribute("data-cluster-font-weight") || "700", 10),
        size: parseInt(el.getAttribute("data-cluster-size") || "40", 10),
      };

      // ✅ NEW: custom SVG marker url (only used when price pins are OFF)
      const customSvgUrl = (el.getAttribute("data-svg-url") || "").trim();

      /* 👇 NEW: device visibility settings for map */
      const mapShowDesktop = truthy(el.getAttribute("data-map-show-desktop") || "true", true);
      const mapShowTablet = truthy(el.getAttribute("data-map-show-tablet") || "false", false);
      const mapShowMobile = truthy(el.getAttribute("data-map-show-mobile") || "false", false);

      /* Preview width for dashboard preview mode */
      const previewWidth = el.getAttribute("data-preview-width") || null;

      /* Legacy/compat */
      const mapboxToken = el.getAttribute("data-mapbox-token") || "";
      const mapStyle = el.getAttribute("data-map-style") || "mapbox://styles/mapbox/streets-v11";

      const account =
        el.getAttribute("data-account-id") ||
        el.getAttribute("data-account") ||
        undefined;

      const apiUrl = el.getAttribute("data-api-url") || undefined;
      const authToken = el.getAttribute("data-auth-token") || undefined;
      const perPageAttr = el.getAttribute("data-per-page");
      const perPage = perPageAttr ? parseInt(perPageAttr, 10) : undefined;
      const debounceAttr = el.getAttribute("data-debounce-ms");
      const debounceMs = debounceAttr ? parseInt(debounceAttr, 10) : undefined;

      /* 👇 NEW: read current query type */
      const currentQuery = el.getAttribute("data-current-query") || "listing";

      /* 🔹 Map settings */
      const mapWheel = truthy(el.getAttribute("data-map-wheel") || "true", true);
      const mapZoomAttr = el.getAttribute("data-map-zoom");
      const mapZoom = mapZoomAttr ? parseInt(mapZoomAttr, 10) : 10;
      const mapCenterLat = parseFloat(el.getAttribute("data-map-center-lat") || "51.5072");
      const mapCenterLng = parseFloat(el.getAttribute("data-map-center-lng") || "-0.1276");

      /* 🔹 Link settings */
      const linkTarget = el.getAttribute("data-link-target") || "_self";

      /* 🔹 Metadata display settings */
      const showGuests = truthy(el.getAttribute("data-show-guests") || "true", true);
      const showBedrooms = truthy(el.getAttribute("data-show-bedrooms") || "true", true);
      const showBathrooms = truthy(el.getAttribute("data-show-bathrooms") || "true", true);
      const showLocation = truthy(el.getAttribute("data-show-location") || "true", true);

      /* 🔹 Price text settings */
      const singularPrefix = el.getAttribute("data-singular-prefix") || "";
      const singularSuffix = el.getAttribute("data-singular-suffix") || "";
      const pluralPrefix = el.getAttribute("data-plural-prefix") || "";
      const pluralSuffix = el.getAttribute("data-plural-suffix") || "";

      /* 🔹 Embedded Search Bar settings */
      const searchBarEnabled = truthy(el.getAttribute("data-search-bar-enabled") || "false", false);
      const searchBarSticky = truthy(el.getAttribute("data-search-bar-sticky") || "false", false);
      const searchBarPadding = parseInt(el.getAttribute("data-search-bar-padding") || "0", 10);
      const searchBarBorderWidth = parseInt(el.getAttribute("data-search-bar-border-width") || "0", 10);
      const searchBarBorderColor = el.getAttribute("data-search-bar-border-color") || "#e5e7eb";
      const searchBarScriptUrl = el.getAttribute("data-search-bar-script-url") || "/search/nexus-search.iife.js";

      /* 🔹 Virtual Scrolling */
      const virtualScroll = truthy(el.getAttribute("data-virtual-scroll") || "false", false);

      /* 🔹 Map icon customization */
      const iconSpec = {
        mode: el.getAttribute("data-map-icon-mode") || "fa",
        faFamily: el.getAttribute("data-map-icon-fa-family") || "fa-solid",
        faIcon: el.getAttribute("data-map-icon-fa-icon") || "fa-location-dot",
        size: parseInt(el.getAttribute("data-map-icon-size") || "28", 10),
        color: el.getAttribute("data-map-icon-color") || "#fe5922",
        svg: el.getAttribute("data-map-icon-svg") || "",
      };

      let columns = { base: 1, md: 3, lg: 4 };
      try {
        if (columnsAttr) columns = JSON.parse(columnsAttr);
      } catch {}

      // Read individual column attributes (from style layer)
      const colSm = el.getAttribute("data-grid-columns-sm");
      const colMd = el.getAttribute("data-grid-columns-md");
      const colLg = el.getAttribute("data-grid-columns-lg");
      const colXl = el.getAttribute("data-grid-columns-xl");
      const colSmMap = el.getAttribute("data-grid-columns-sm-map");
      const colMdMap = el.getAttribute("data-grid-columns-md-map");
      const colLgMap = el.getAttribute("data-grid-columns-lg-map");
      const colXlMap = el.getAttribute("data-grid-columns-xl-map");

      // Build both column sets - Widget will choose based on actual map visibility per device
      const columnsNoMap = {
        base: colSm ? parseInt(colSm, 10) : columns.base,
        sm: colSm ? parseInt(colSm, 10) : columns.base,
        md: colMd ? parseInt(colMd, 10) : columns.md,
        lg: colLg ? parseInt(colLg, 10) : columns.lg,
        xl: colXl ? parseInt(colXl, 10) : columns.xl || columns.lg,
      };
      const columnsWithMap = {
        base: colSmMap ? parseInt(colSmMap, 10) : columnsNoMap.base,
        sm: colSmMap ? parseInt(colSmMap, 10) : columnsNoMap.sm,
        md: colMdMap ? parseInt(colMdMap, 10) : columnsNoMap.md,
        lg: colLgMap ? parseInt(colLgMap, 10) : columnsNoMap.lg,
        xl: colXlMap ? parseInt(colXlMap, 10) : columnsNoMap.xl,
      };

      // Store or retrieve the root for this element
      if (!el._nexusGridRoot) {
        el._nexusGridRoot = createRoot(el);
      }

      el._nexusGridRoot.render(
        <Widget
          key={`widget-${isGoogle ? 'google' : 'leaflet'}-${account || 'default'}`}
          theme={theme}
          columns={columns}
          columnsNoMap={columnsNoMap}
          columnsWithMap={columnsWithMap}
          gap={gap}
          radius={radius}
          cardAspect={cardAspect}
          shadow={shadow}
          map={map}
          mapHeight={mapHeight}
          mapHeightUnit={mapHeightUnit}
          /* Google vs Leaflet + key */
          isGoogle={isGoogle}
          googleApiKey={googleApiKey}
          /* compat */
          mapboxToken={mapboxToken}
          mapStyle={mapStyle}
          iconSpec={iconSpec}
          account={account}
          apiUrl={apiUrl}
          authToken={authToken}
          perPage={perPage}
          debounceMs={debounceMs}
          /* ✅ new toggle passed to widget */
          isPricePins={isPricePins}
          enableClustering={enableClustering}
          clusterStyle={clusterStyle}
          customSvgUrl={customSvgUrl}
          currentQuery={currentQuery}
          /* Device visibility */
          mapShowDesktop={mapShowDesktop}
          mapShowTablet={mapShowTablet}
          mapShowMobile={mapShowMobile}
          previewWidth={previewWidth}
          /* Map settings */
          mapWheel={mapWheel}
          mapZoom={mapZoom}
          mapCenter={{ lat: mapCenterLat, lng: mapCenterLng }}
          /* Link settings */
          linkTarget={linkTarget}
          /* Metadata display */
          showGuests={showGuests}
          showBedrooms={showBedrooms}
          showBathrooms={showBathrooms}
          showLocation={showLocation}
          /* Price text */
          singularPrefix={singularPrefix}
          singularSuffix={singularSuffix}
          pluralPrefix={pluralPrefix}
          pluralSuffix={pluralSuffix}
          /* Embedded Search Bar */
          searchBarEnabled={searchBarEnabled}
          searchBarSticky={searchBarSticky}
          searchBarPadding={searchBarPadding}
          searchBarBorderWidth={searchBarBorderWidth}
          searchBarBorderColor={searchBarBorderColor}
          searchBarScriptUrl={searchBarScriptUrl}
          virtualScroll={virtualScroll}
        />
      );
  }

  // Add preconnect hints for faster API/CDN connections
  function addPreconnect(url) {
    if (!url || document.querySelector(`link[rel="preconnect"][href="${url}"]`)) return;
    const link = document.createElement("link");
    link.rel = "preconnect";
    link.href = url;
    link.crossOrigin = "anonymous";
    document.head.appendChild(link);
  }

  // Initial render
  ready(() => {
    // Preconnect to API and common CDNs
    addPreconnect("https://api.nexus.design");
    addPreconnect("https://fonts.googleapis.com");
    addPreconnect("https://fonts.gstatic.com");

    const nodes = document.querySelectorAll(
      "#nexus-grid-widget, .nexus-grid-widget, [data-nexus-grid]"
    );
    if (!nodes.length) {
      logger.warn("[nexus-grid] mount element not found");
      return;
    }

    nodes.forEach((el) => {
      renderWidget(el);
    });
  });

  // Expose reload function globally for live preview updates
  window.NexusGridReload = function() {
    logger.log("[nexus-grid] Reloading widget with updated attributes...");
    const nodes = document.querySelectorAll(
      "#nexus-grid-widget, .nexus-grid-widget, [data-nexus-grid]"
    );
    nodes.forEach((el) => {
      const isGoogle = truthy(el.getAttribute("data-is-google") || "false", false);
      logger.log("[nexus-grid] Map provider:", isGoogle ? "Google Maps" : "Leaflet");
      renderWidget(el);
    });
  };
})();
